/**
 * Auth Store
 * Versioned session storage with OfficeRuntime.storage / localStorage fallback.
 * Token refresh with deduplication.
 */

/* global OfficeRuntime */

import { STORAGE_KEYS } from "../constants";
import { logger } from "../logger";
import { getPublicConfig } from "../../excel/config/getPublicConfig";
import type { AuthSession } from "./types";

const STORAGE_KEY = STORAGE_KEYS.AUTH_SESSION;
const BLOB_VERSION = 1;
const REFRESH_DEDUPE_MS = 30000;

interface StoredBlob {
  v: number;
  session: AuthSession;
}

let lastRefreshFailureAt = 0;

async function getStorage(): Promise<Storage> {
  try {
    if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
      return {
        getItem: async (k: string) => (await OfficeRuntime.storage.getItem(k)) as string | null,
        setItem: async (k: string, v: string) => {
          await OfficeRuntime.storage.setItem(k, v);
        },
        removeItem: async (k: string) => {
          await OfficeRuntime.storage.removeItem(k);
        },
      };
    }
  } catch {
    // OfficeRuntime unavailable
  }
  return {
    getItem: async (k: string) => localStorage.getItem(k),
    setItem: async (k: string, v: string) => {
      localStorage.setItem(k, v);
    },
    removeItem: async (k: string) => {
      localStorage.removeItem(k);
    },
  };
}

export async function load(): Promise<AuthSession | null> {
  const storage = await getStorage();
  const raw = await storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const blob = JSON.parse(raw) as StoredBlob;
    if (blob.v !== BLOB_VERSION || !blob.session) return null;
    const s = blob.session;
    if (
      !s.user?.id ||
      !s.user?.email ||
      !s.access_token ||
      !s.refresh_token ||
      typeof s.expires_at !== "number"
    ) {
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export async function save(session: AuthSession): Promise<void> {
  const storage = await getStorage();
  const blob: StoredBlob = { v: BLOB_VERSION, session };
  await storage.setItem(STORAGE_KEY, JSON.stringify(blob));
}

export async function clear(): Promise<void> {
  const storage = await getStorage();
  await storage.removeItem(STORAGE_KEY);
}

export function isExpired(session: AuthSession, bufferSec: number = 120): boolean {
  return session.expires_at - bufferSec <= Math.floor(Date.now() / 1000);
}

export async function getValidAccessToken(): Promise<string | null> {
  const session = await load();
  if (!session) return null;
  if (!isExpired(session)) {
    return session.access_token;
  }
  const now = Date.now();
  if (now - lastRefreshFailureAt < REFRESH_DEDUPE_MS) {
    return null;
  }
  try {
    const config = await getPublicConfig();
    const baseUrl = config.public.apiBaseUrl.replace(/\/$/, "");
    const res = await fetch(baseUrl + "/v1/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });
    if (!res.ok) {
      lastRefreshFailureAt = now;
      logger.log("AuthStore", "refresh failed status", res.status);
      return null;
    }
    const data = (await res.json()) as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
    };
    const newToken = data.access_token;
    const newRefresh = data.refresh_token ?? session.refresh_token;
    const newExpires = typeof data.expires_at === "number" ? data.expires_at : session.expires_at;
    if (!newToken) {
      lastRefreshFailureAt = now;
      return null;
    }
    const updated: AuthSession = {
      ...session,
      access_token: newToken,
      refresh_token: newRefresh,
      expires_at: newExpires,
    };
    await save(updated);
    logger.log("AuthStore", "refresh success", "");
    return newToken;
  } catch (err) {
    lastRefreshFailureAt = now;
    logger.error("AuthStore", "refresh failed", err);
    return null;
  }
}

export const authStore = {
  load,
  save,
  clear,
  isExpired,
  getValidAccessToken,
};
